import React, { useRef, useState } from "react";
import { useParams, useHistory, Prompt } from "react-router-dom";
import { useSelector } from "react-redux";
import { Typography, Stack } from "@mui/material";
import { RELATIONSHIPS } from "@constants";
import * as selectors from "selectors";
import useGetFinancialGuarantor from "hooks/api/client/useGetFinancialGuarantor";
import { useGetTeams } from "hooks/api/useGetTeams";
import { useGetTeamMembers } from "hooks/api/useGetTeamMembers";
import { useGetTeamRoles } from "hooks/api/useGetTeamRoles";
import useUpdateGuarantor from "hooks/api/client/useUpdateFinancialGuarantor";
import SectionWrapper from "../../Widgets/SectionWrapper";
import { ANLoadingScreen } from "@answersnow/pkg-theme";
import ViewFields from "./ViewFields";
import EditFields, { type EditFieldsHandle } from "./EditFields";
import { useGlobalToast } from "components/GlobalToastProvider";
import { teamMembersWithClient } from "../Utils/teamMembersWithClient";
import SaveBeforeNavDialog from "../../Dialogs/SaveBeforeNavDialog";
import { useCustomerDetails } from "hooks/useCustomerDetails";

const normalizeRelationshipValue = (value?: string): string => {
  return value?.trim().toLowerCase().replace(/[\s-]+/g, "_") ?? "";
};

/**
 * FinancialGuarantor component that manages its own state and data fetching.
 * This component handles financial guarantor information independently with its own edit mode.
 * Extracts clientId from URL parameters - no props required.
 */
const FinancialGuarantor: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const history = useHistory();
  const isCaregiver = useSelector(selectors.isCaregiver);
  const userPermissions: any = useSelector(selectors.getUserPermissionsList);
  const canEdit = !isCaregiver && Boolean(userPermissions?.add_client_insurance);
  const { showToast } = useGlobalToast();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [openWarning, setOpenWarning] = useState<boolean>(false);
  const [nextLocation, setNextLocation] = useState<string | null>(null);
  const editFieldsRef = useRef<EditFieldsHandle>(null);

  // Fetch guarantor data
  const { guarantor, guarantorLoading, refetchGuarantorData } = useGetFinancialGuarantor(clientId);

  // Fetch client data
    const {
      data: customerDetails,
      isLoading: clientsLoading,
    } = useCustomerDetails(clientId);

  // Fetch team members for dropdown
  const { teams, teamsLoading } = useGetTeams(clientId ? { clientIds: clientId } : null);
  const teamId = teams?.[0]?.id;
  const { teamMembers, teamMembersLoading } = useGetTeamMembers({
    teamIds: teamId,
    includeTeamInvitees: false,
  });
  const { teamRoles, teamRolesLoading } = useGetTeamRoles({ teamIds: teamId });

  const roleNameToRelationshipKey = React.useMemo(() => {
    return Object.entries(RELATIONSHIPS).reduce(
      (acc, [key, label]) => {
        acc[normalizeRelationshipValue(label)] = key;
        return acc;
      },
      {} as Record<string, string>
    );
  }, []);

  const roleIdToRelationshipKey = React.useMemo(() => {
    const map: Record<number, string> = {};

    teamRoles?.forEach((role: any) => {
      const normalizedRoleName = normalizeRelationshipValue(role?.name);
      const relationshipKey =
        (normalizedRoleName in RELATIONSHIPS
          ? normalizedRoleName
          : roleNameToRelationshipKey[normalizedRoleName]) || "";

      if (relationshipKey) {
        map[Number(role.id)] = relationshipKey;
      }
    });

    return map;
  }, [teamRoles, roleNameToRelationshipKey]);

  // Combine team members with client data
  const teamMembersWithClientData = React.useMemo(() => {
    const combined = teamMembersWithClient(teamMembers, customerDetails);
    return combined;
  }, [teamMembers, customerDetails]);

  const teamMembersList = teamMembersWithClientData
    ?.filter((member) => member.hasAcceptedInvite)
    .map((member) => ({
      // Include all member data for the form
      ...member,
      value: member?.isMainClient ? member.clientId : member.userId,
      label: member.name,
      relationshipToClient:
        member.relationshipToClient ??
        member.relationship ??
        roleIdToRelationshipKey[Number(member.roleId)] ??
        "",
    }));

  // Update mutation
  const { updateGuarantorMutation } = useUpdateGuarantor({
    onSuccess: () => {
      refetchGuarantorData();
      setEditMode(false);
      setHasUnsavedChanges(false);
      showToast({ message: "Financial guarantor updated successfully" });
    },
  });

  const handleEdit = (): void => {
    setEditMode(true);
  };

  const resetFormAndExitEditMode = (): void => {
    setEditMode(false);
    setHasUnsavedChanges(false);
    setNextLocation(null);
  };

  const handleCancel = (): void => {
    // Check for unsaved changes
    if (hasUnsavedChanges) {
      setOpenWarning(true);
      return;
    }
    resetFormAndExitEditMode();
  };

  const handleSave = (): void => {
    editFieldsRef.current?.submit();
  };

  const handleFormSubmit = (values: any): void => {
    // Remove teamMemberId from values
    const formValues = { ...values };
    delete formValues.teamMemberId;

    updateGuarantorMutation({
      ...formValues,
      clientId,
    });
  };

  const handleDirtyChange = (isDirty: boolean): void => {
    setHasUnsavedChanges(isDirty);
  };

  const handleContinueWithoutSaving = (): void => {
    setOpenWarning(false);
    resetFormAndExitEditMode();
    if (nextLocation) {
      history.push(nextLocation);
    }
  };

  if (clientsLoading || teamsLoading || teamMembersLoading || teamRolesLoading || guarantorLoading) {
    return <ANLoadingScreen />;
  }

  if (!clientId) {
    return (
      <Typography variant="h6" color="error">
        Client ID is required
      </Typography>
    );
  }

  return (
    <>
      {/* Prevent navigation if there are unsaved changes */}
      {/* @ts-expect-error - Type incompatibility between react-router-dom and React types */}
      <Prompt
        when={hasUnsavedChanges && !nextLocation}
        message={(location) => {
          setNextLocation(location.pathname);
          setOpenWarning(true);
          return false;
        }}
      />

      <SectionWrapper
        title="Financial Guarantor"
        description="The party that provides financial backing to ensure that claims will be paid"
        editMode={editMode}
        onEdit={canEdit ? handleEdit : undefined}
        onCancel={handleCancel}
        onSave={handleSave}
        saveDisabled={!hasUnsavedChanges}
      >
        <Stack width="100%">
          {editMode ? (
            <EditFields
              ref={editFieldsRef}
              clientId={clientId}
              guarantor={guarantor}
              teamMembersList={teamMembersList}
              onSave={handleFormSubmit}
              onDirtyChange={handleDirtyChange}
            />
          ) : (
            <ViewFields guarantor={guarantor} />
          )}
        </Stack>
      </SectionWrapper>

      {/* Warning dialog for unsaved changes */}
      <SaveBeforeNavDialog
        saveBeforeNavOpen={openWarning}
        onToggleSaveBeforeNav={() => {
          setOpenWarning(false);
          setNextLocation(null);
        }}
        onContinueWithoutSaving={handleContinueWithoutSaving}
        severetyAlert="warning"
        primaryActionColorButton="error"
        secondaryText="Stay on Page"
        isCancelClicked
      />
    </>
  );
};

export default FinancialGuarantor;
